import React, { useEffect, useState } from "react";
import { Compass, Star, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { termsApi } from "@/lib/apiClient";
import { useTranslation } from "@/context/LanguageContext";

/**
 * Adaptive Learning Profile — the concepts a learner keeps looking up while
 * reading. Deliberately framed as gentle signals ("frequently explored", "may
 * need revision"), NOT hard "knowledge gaps" (that needs stronger signals like
 * quiz/flashcard performance).
 */
export const LearningProfile = () => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try { setData(await termsApi.profile()); }
            catch (e) { setData(null); }
            finally { setLoading(false); }
        })();
    }, []);

    const terms = data?.top_terms || [];

    return (
        <div className="rounded-2xl border border-border bg-surface-1 p-5" data-testid="learning-profile-card">
            <div className="mb-1 flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]"><Compass className="h-4 w-4" /></div>
                <h3 className="font-sans-display text-sm font-semibold text-t1">{t("learningProfile.title")}</h3>
            </div>
            <p className="text-sm text-t3">{t("learningProfile.subtitle")}</p>

            {loading ? (
                <div className="flex items-center gap-2 py-6 text-t3"><Loader2 className="h-4 w-4 animate-spin text-[hsl(var(--primary))]" /></div>
            ) : terms.length === 0 ? (
                <div data-testid="learning-profile-empty" className="mt-4 rounded-xl border border-dashed border-border bg-surface-2/40 px-4 py-6 text-center text-sm text-t3">
                    {t("learningProfile.empty")}
                </div>
            ) : (
                <>
                    <div className="mt-4 flex flex-wrap gap-2" data-testid="learning-profile-terms">
                        {terms.slice(0, 18).map((tm, i) => {
                            const revisit = (tm.count || 0) >= 3;
                            return (
                                <span
                                    key={i}
                                    data-testid="profile-term-chip"
                                    className={cn(
                                        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs",
                                        revisit ? "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 text-t1" : "border-border bg-surface-2 text-t2"
                                    )}
                                >
                                    <span className="max-w-[160px] truncate">{tm.term}</span>
                                    <span className="font-mono-ui text-[10px] text-t4">{tm.count}×</span>
                                    {revisit && <span className="mono-label text-[9px] text-[hsl(var(--warning))]">{t("learningProfile.revisit")}</span>}
                                </span>
                            );
                        })}
                    </div>
                    {data?.saved_count > 0 && (
                        <div className="mt-4 flex items-center gap-1.5 text-xs text-t3">
                            <Star className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> {t("learningProfile.saved", { n: data.saved_count })}
                        </div>
                    )}
                    {data?.review?.length > 0 && (
                        <div className="mt-4" data-testid="profile-review-section">
                            <div className="mono-label mb-1.5 text-t4">{t("learningProfile.suggestedReview")}</div>
                            <div className="flex flex-wrap gap-2">
                                {data.review.slice(0, 12).map((r, i) => (
                                    <span key={i} data-testid="profile-review-chip" className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-3 py-1.5 text-xs text-t1">
                                        {r.term}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
